import {useState, useMemo, useEffect} from "react";
import PropTypes from "prop-types";
import Loader from "../../../common/loader/Loader";
import {CONTENT_TYPE} from "../widget-constants";
import DateIntervalSelect from "./date-interval-select/DateIntervalSelect";
import "./activity-count.scss";
import {
    CUSTOM_DATE_INTERVAL_VALUE,
    DATE_INTERVALS,
    DATE_INTERVAL_DIFFERENCE,
    LABELS_MAPPING,
} from "./constants";
import ActivityCountCalendar from "./ActivityCountCalendar";
import ModalDefault from "../../../common/modal/modal-default";
import ActivityCountConfigurationForm from "./configuration-form/ActivityCountConfigurationForm";
import ActivityCountRotator from "./activity-count-rotator/ActivityCountRotator";
import PieChart from "../../../common/charts/pie-chart/pie-chart";
import DashboardService from "../../../../services/dashboard-service";
import {formatDataChartData} from "../../../../helpers/chart-helpers";
import WidgetEmpty from "../empty-widget-content/empty-widget-content";
import cloneDeep from "lodash/cloneDeep";
import {steveDataStorage} from "../../../../helpers/sdk-helpers";
import WidgetHeader from "../widget-header/WidgetHeader";

const INITIAL_VALUES = {
    rotateDatasetsInterval: 5,
    dateInterval: DATE_INTERVALS[2].value,
    autoRefresh: {value: 300000, label: "Every 5 minutes"},
};

const NO_DATA_FOR_COUNT_TYPE = "0";

const getDateWithDifference = (dayDifference) => {
    if (dayDifference === -1) {
        // when all is selected set date to 1. Jan 2018
        return new Date(2018, 0, 1);
    }
    const targetDate = new Date();
    targetDate.setHours(0, 0, 0, 0);
    targetDate.setDate(targetDate.getDate() - dayDifference);
    return new Date(targetDate);
};

const ActivityCount = ({
    showWidgetInfo,
    onRemove,
    onClone,
    onUpdate,
    isEditAllowed,
    data,
    isLightThemeActive,
}) => {
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [configFormData, setConfigFormData] = useState({
        title: data.title,
        datasets: data.config.datasets || [],
        rotateDatasetsInterval:
            data.config.rotateDatasetsInterval || INITIAL_VALUES.rotateDatasetsInterval,
        autoRefresh: data.config.autoRefresh || INITIAL_VALUES.autoRefresh,
    });
    const [isSettingsModalShown, setIsSettingsModalShown] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedDateInterval, setSelectedDateInterval] = useState(INITIAL_VALUES.dateInterval);
    const [tempDateInterval, setTempDateInterval] = useState(INITIAL_VALUES.dateInterval);
    const [startIntervalDate, setStartIntervalDate] = useState(null);
    const dateIntervalLabel = useMemo(
        () =>
            selectedDateInterval !== "all"
                ? `Since ${startIntervalDate?.toLocaleDateString()}`
                : "All available",
        [startIntervalDate, selectedDateInterval]
    );
    const [formattedChartData, setFormattedChartData] = useState({});
    const [currentCountType, setCurrentCountType] = useState("");
    const totalChangeCount = useMemo(
        () =>
            formattedChartData?.[currentCountType]?.datasets?.[0]?.data?.reduce(
                (acc, item) => acc + item
            ) || "0",
        [formattedChartData, currentCountType]
    );
    const [steveStorageDataLoaded, setSteveStorageDataLoaded] = useState(false);

    const handleCountTypeChange = (value) => setCurrentCountType(value);

    useEffect(() => {
        const dataFromSteveStorage = steveDataStorage("activityCount", "dateInterval", false).get(
            data.id
        );
        setStartIntervalDate(
            dataFromSteveStorage?.startInterval || dataFromSteveStorage?.startInterval === 0
                ? new Date(dataFromSteveStorage.startInterval)
                : getDateWithDifference(DATE_INTERVAL_DIFFERENCE[INITIAL_VALUES.dateInterval])
        );
        setSelectedDateInterval(
            dataFromSteveStorage?.selectedInterval || INITIAL_VALUES.dateInterval
        );
        setTempDateInterval(dataFromSteveStorage?.selectedInterval || INITIAL_VALUES.dateInterval);
        setSteveStorageDataLoaded(true);
    }, [data]);

    useEffect(() => {
        if (
            !steveStorageDataLoaded ||
            !configFormData?.autoRefresh?.value ||
            !configFormData.datasets.length
        ) {
            return;
        }

        const formatChartData = (totalCountStatistics) => {
            if (!configFormData.datasets.length || !Object.keys(totalCountStatistics).length) {
                return;
            }

            const formattedChartData = Object.entries(totalCountStatistics).reduce(
                (chartData, [countType, countTypeStats]) => {
                    const labels = LABELS_MAPPING[countType];
                    const formattedDataForCountType = Object.keys(labels).reduce(
                        (acc, label) => ({
                            ...acc,
                            [labels[label]]: countTypeStats[label],
                        }),
                        {}
                    );
                    return {
                        ...chartData,
                        [countType]: formatDataChartData(formattedDataForCountType, {
                            label: 10,
                            value: 10,
                        }),
                    };
                },
                {}
            );
            return formattedChartData;
        };

        const fetchTotalCountStatistics = async () => {
            setIsLoadingData(true);
            const countTypes = configFormData.datasets.map((item) => item.value);
            const totalCountStatistics = await DashboardService.getTotalCountActivityStatistics(
                startIntervalDate.getTime(),
                Date.now(),
                countTypes
            );
            const formattedChartData = formatChartData(totalCountStatistics);
            if (Object.keys(formattedChartData).length) {
                setCurrentCountType(configFormData.datasets[0].value);
            }
            setFormattedChartData(formattedChartData);
            setIsLoadingData(false);
        };

        fetchTotalCountStatistics();

        const fetchInterval = setInterval(
            () => fetchTotalCountStatistics(),
            configFormData.autoRefresh.value
        );

        return () => {
            clearInterval(fetchInterval);
        };
    }, [configFormData, startIntervalDate, isLightThemeActive, steveStorageDataLoaded]);

    const toggleSettingsModal = (e) => {
        e.stopPropagation();
        setIsSettingsModalShown((value) => !value);
    };

    const getDayDifference = (interval) => {
        switch (interval) {
            case CUSTOM_DATE_INTERVAL_VALUE:
                setShowCalendar(true);
                setSelectedDateInterval("calendar");
                return 0;
            case "lastDay":
                return 1;
            case "lastWeek":
                return 7;
            case "lastMonth":
                return 30;
            case "all":
                return -1;
            default:
                return 0;
        }
    };

    const handleIntervalSelect = (interval) => {
        const dayDifference = getDayDifference(interval);
        if (!dayDifference) {
            return;
        }
        const dateWithDifference = getDateWithDifference(dayDifference);
        steveDataStorage("activityCount", "dateInterval", false).set(
            {
                startInterval: dateWithDifference.getTime(),
                selectedInterval: interval,
            },
            data.id
        );
        setStartIntervalDate(dateWithDifference);
        if (interval !== "calendar") {
            setTempDateInterval(interval);
        }
        setSelectedDateInterval(interval);
    };

    const handleDateSelect = (date) => {
        steveDataStorage("activityCount", "dateInterval", false).set(
            {
                startInterval: date.getTime(),
                selectedInterval: CUSTOM_DATE_INTERVAL_VALUE,
            },
            data.id
        );
        setStartIntervalDate(date);
        setSelectedDateInterval(CUSTOM_DATE_INTERVAL_VALUE);
        setTempDateInterval(CUSTOM_DATE_INTERVAL_VALUE);
        setShowCalendar(false);
    };

    const closeCalendar = () => {
        setSelectedDateInterval(tempDateInterval);
        setShowCalendar(false);
    };

    const handleSave = async (values) => {
        setIsSettingsModalShown(false);
        setConfigFormData(values);
        onUpdate({
            ...data,
            title: values.title,
            config: {
                ...data.config,
                datasets: values.datasets,
                rotateDatasetsInterval: values.rotateDatasetsInterval,
                autoRefresh: values.autoRefresh,
            },
        });
    };

    const getContentType = () => {
        if (!configFormData.datasets.length) {
            return CONTENT_TYPE.NOT_INITIALIZED;
        }
        // TODO check if this is empty data state, check if there is error status
        if (!formattedChartData || !Object.keys(formattedChartData).length) {
            return CONTENT_TYPE.EMPTY;
        }
        return CONTENT_TYPE.DATA;
    };

    const renderContent = () => {
        switch (getContentType()) {
            case CONTENT_TYPE.NOT_INITIALIZED:
                return (
                    <WidgetEmpty
                        isEditAllowed={isEditAllowed}
                        showSettingsModal={toggleSettingsModal}
                    />
                );
            case CONTENT_TYPE.EMPTY:
                return (
                    <WidgetEmpty
                        isEditAllowed={isEditAllowed}
                        showSettingsModal={toggleSettingsModal}
                        text="No data for specified configuration"
                    />
                );
            case CONTENT_TYPE.DATA:
                return (
                    <>
                        <div className="activity-count__separator activity-count--inner"></div>
                        <div className="activity-count__date-interval">
                            <div className="activity-count__date-interval-label">
                                {dateIntervalLabel}
                            </div>
                            <DateIntervalSelect
                                onSelect={handleIntervalSelect}
                                initialValue={selectedDateInterval}
                            />
                        </div>
                        <div className="activity-count__stats">
                            <div className="activity-count__stats--inner">
                                {totalChangeCount !== NO_DATA_FOR_COUNT_TYPE &&
                                currentCountType &&
                                Object.keys(formattedChartData).length &&
                                formattedChartData[currentCountType] &&
                                Object.keys(formattedChartData[currentCountType]).length ? (
                                    <div className="activity-count__chart--wrapper">
                                        <PieChart
                                            data={cloneDeep(formattedChartData)[currentCountType]}
                                            cutoutPercentage={40}
                                        />
                                    </div>
                                ) : (
                                    <div className="activity-count__no-data-msg glu-h6 text-bold">
                                        <span className="g-icon g-icon-info" />
                                        No activity for the selected time period
                                    </div>
                                )}
                                <div className="activity-count__rotator">
                                    {configFormData.datasets.length ? (
                                        <ActivityCountRotator
                                            datasets={configFormData.datasets}
                                            onChange={handleCountTypeChange}
                                            totalChangeCount={totalChangeCount}
                                            rotateInterval={configFormData.rotateDatasetsInterval}
                                            totalCountDataChanged={!isLoadingData}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="glu-content glu-dashboard-widget activity-count">
                {(isLoadingData || !steveStorageDataLoaded) && <Loader />}
                {showCalendar && (
                    <ModalDefault isOpen={showCalendar}>
                        <div className="glu-modal-content">
                            <div className="glu-modal__header">
                                <div className="glu-modal__title glu-flex-grow">Set a date</div>
                                <div className="glu-flex-shrink">
                                    <button
                                        aria-label="close"
                                        type="button"
                                        className="glu-btn-link g-icon g-icon-close"
                                        onClick={closeCalendar}
                                    />
                                </div>
                            </div>
                            <ActivityCountCalendar
                                date={startIntervalDate}
                                onClose={closeCalendar}
                                onDateSet={handleDateSelect}
                            />
                        </div>
                    </ModalDefault>
                )}
                <WidgetHeader
                    isEditAllowed={isEditAllowed}
                    title={configFormData.title}
                    showWidgetInfo={showWidgetInfo(data)}
                    openConfiguration={toggleSettingsModal}
                    onRemove={onRemove(data)}
                    onClone={onClone(data)}
                />
                <div className="glu-content__body activity-count__content">{renderContent()}</div>
                {isSettingsModalShown && (
                    <ModalDefault isOpen={isSettingsModalShown}>
                        <div className="glu-modal-content">
                            <div className="glu-modal__header">
                                <div className="glu-modal__title glu-flex-grow">
                                    {configFormData.title}
                                </div>
                                <div className="glu-flex-shrink">
                                    <button
                                        aria-label="close"
                                        type="button"
                                        className="glu-btn-link g-icon g-icon-close"
                                        onClick={toggleSettingsModal}
                                    />
                                </div>
                            </div>
                            <ActivityCountConfigurationForm
                                onFormSubmit={handleSave}
                                onCancel={toggleSettingsModal}
                                initialValues={configFormData}
                            />
                        </div>
                    </ModalDefault>
                )}
            </div>
        </>
    );
};

ActivityCount.propTypes = {
    data: PropTypes.objectOf(PropTypes.any).isRequired,
    isEditAllowed: PropTypes.bool,
    onRemove: PropTypes.func,
    onUpdate: PropTypes.func,
    showWidgetInfo: PropTypes.func,
    onClone: PropTypes.func,
};

ActivityCount.defaultProps = {
    isEditAllowed: false,
    onRemove: undefined,
    onUpdate: undefined,
    showWidgetInfo: undefined,
    onClone: undefined,
};

export default ActivityCount;
